import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import {
  BehaviorSubject,
  combineLatest,
  from,
  interval,
  map,
  of,
  shareReplay,
  startWith,
} from 'rxjs';
import { delayWhen, filter, switchMap, tap } from 'rxjs/operators';

import {
  NewRequestDialogComponent,
  NewRequestResult,
} from '../../dialogs/new-request-dialog/new-request-dialog.component';
import { RouteManager } from '../../../app-routing.module';
import { Logging } from '../../../utils/logging';
import { RequestService } from '../../../services/request/request.service';
import { ContractNumberSearchModel } from '../../../models/ContractNumberSearchModel';
import { ProductSourceTypeModel } from '../../../models/ProductSourceTypeModel';
import { IncumbentDropdownOptions } from '../../dialogs/request-target-search/request-target-search.component';
import {
  DEFAULT_PAGE_EVENT,
  DEFAULT_PAGE_SIZE_OPTIONS,
  defaultSortState,
  getPaginatedData,
} from '../../../utils/table-utils';
import { VccSearchModel } from '../../dialogs/request-target-search/vcc-request-search-modal/vcc-request-search-modal.component';

export interface RequestData {
  requestId: number;
  supplierName: string;
  vcc: string;
  contractNumber: string;
  lastModifiedDateUtc: string;
  status: string;
}

@Component({
  selector: 'app-my-requests',
  templateUrl: './my-requests.component.html',
  styleUrls: ['./my-requests.component.scss'],
  standalone: false,
})
export class MyRequestsComponent {
  public pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  public DEFAULT_SORT_OPTION = {
    active: 'lastModifiedDateUtc',
    direction: 'desc',
  } as Sort;
  public requestColumns = [
    'requestId',
    'vcc',
    'supplierName',
    'contractNumber',
    'lastModifiedDateUtc',
    'status',
  ];

  constructor(
    private dialog: MatDialog,
    private requestService: RequestService,
    private snackBar: MatSnackBar,
  ) {}

  private readonly _pageEvent$ = new BehaviorSubject<PageEvent>(
    DEFAULT_PAGE_EVENT,
  );
  private readonly _sortState$ = new BehaviorSubject<Sort>(
    this.DEFAULT_SORT_OPTION,
  );

  protected readonly fetchingRequest$ = new BehaviorSubject<boolean>(true);
  protected readonly disableReload$ = this.fetchingRequest$.pipe(
    delayWhen(fetching => (!fetching ? interval(3000) : of(false))),
  );

  private readonly _requests$ = this.fetchingRequest$.pipe(
    filter(fetching => fetching),
    switchMap(_ =>
      from(this.requestService.listRfxForUser()).pipe(
        map(response => response.requests),
        tap(_ => this.fetchingRequest$.next(false)),
        startWith([] as RequestData[]),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly vm$ = combineLatest([
    this._requests$,
    this._sortState$,
    this._pageEvent$,
  ]).pipe(
    map(([requests, sortState, pageEvent]) => {
      const sortedRequests = this.sortRequests(requests, sortState);
      pageEvent.length = sortedRequests.length;
      const paginatedRequests = getPaginatedData(sortedRequests, pageEvent);

      return { requests, paginatedRequests, sortState, pageEvent };
    }),
  );

  reloadRequests = () => {
    this.fetchingRequest$.next(true);
  };

  openNewRequestModal = () => {
    return this.dialog
      .open(NewRequestDialogComponent, {
        panelClass: 'new-request-dialog-container',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result: NewRequestResult) => this.onModalClose(result));
  };

  onModalClose(result: NewRequestResult) {
    if (result == null) {
      return;
    } else {
      const memberIdList = result.filterCriteria.map(member => member.memberId);

      switch (result.resultType) {
        case IncumbentDropdownOptions.CONTRACT_NUMBER:
          const value = result.value as ContractNumberSearchModel;

          return this.requestService
            .createRfx(value.contractId, ProductSourceTypeModel.CONTRACT, {
              memberIdList,
            })
            .then(rfxHeaderKey => {
              this.fetchingRequest$.next(true);
              return this.rfxCreateSuccessSnackbar(
                `Request ${rfxHeaderKey} created successfully for Contract ${value.contractNumber}.`,
              );
            })
            .catch(e => this.rfxCreateErrorSnackbar(e));
        case IncumbentDropdownOptions.VCC:
          const vccModel = result.value as VccSearchModel;
          return this.requestService
            .createRfx(vccModel.vccId, ProductSourceTypeModel.VCC, {
              memberIdList,
            })
            .then(rfxHeaderKey => {
              this.fetchingRequest$.next(true);
              return this.rfxCreateSuccessSnackbar(
                `Request ${rfxHeaderKey} created successfully for VCC ${vccModel.vccName}.`,
              );
            })
            .catch(e => this.rfxCreateErrorSnackbar(e));
      }
    }
  }

  rfxCreateErrorSnackbar(error: any) {
    Logging.error('Error saving incumbent:', error);
    return this.snackBar.open(
      'An error occurred while saving the incumbent:',
      'Close',
      {
        verticalPosition: 'top',
        duration: 5000,
      },
    );
  }

  rfxCreateSuccessSnackbar(message: string) {
    return this.snackBar.open(message, 'Close', {
      verticalPosition: 'top',
      duration: 5000,
    });
  }

  handlePageEvent(pageEvent: PageEvent) {
    this._pageEvent$.next(pageEvent);
  }

  handleSortEvent(sortState: Sort) {
    this._sortState$.next(sortState);
  }

  private sortRequests(requests: RequestData[], sortState: Sort) {
    if (!sortState.active || sortState.direction === '') {
      return requests;
    } else {
      return requests.sort((a, b) => {
        switch (sortState.active) {
          default:
            return defaultSortState(a, b, sortState);
        }
      });
    }
  }

  protected readonly RouteManager = RouteManager;
}
